import type { GitError } from '../wasm-git-worker-errors.model';

export type GitFileTree = Record<GitTreeItem['name'], GitTreeItem>;
export type GitFileTreeWithContents<T = unknown> = Record<GitTreeItem['name'], GitTreeItemWithContent<T>>;

export type FilePath = string;
export type FileContent = string;

export enum ConflictSide {
  YOURS,
  THEIRS,
}

export interface GitBranch {
  name: string;
  tree: GitFileTree;
  numberOfFiles?: number;
  local?: boolean;
  published?: boolean;
  committerDate?: Date;
  default?: boolean;
}

export interface GitBranchWithAheadBehindStatus extends GitBranch {
  aheadBehindStatus: GitAheadBehindStatus;
}

export interface GitStashEntry {
  index: string;
  branchName: string;
  sha: string;
}

export interface GitRef {
  sha: string;
  name: string;
  upstream?: string;
}

export interface GitTreeItem {
  type: 'dir' | 'file';
  path: FilePath;
  name: string;

  children?: GitFileTree;
}

export interface GitTreeItemWithContent<T = unknown> extends GitTreeItem {
  children?: GitFileTreeWithContents<T>;
  /**
   * JSON.parsed file contents if it ends with ".json",
   * otherwise this will just be the file contents in string format
   */
  content?: T;
  mtime?: number;
}

export interface GitCommit {
  hash: string;
  parentHash: string;
  title: string;
  description: string;
  authorName: string;
  authorEmail: string;
  date: Date;
  isMergeCommit?: boolean;
}

export interface GitSearchMatch {
  leftPart: string;
  match: string;
  rightPart: string;
  line: number;
  col: number;
}

export interface GitSearchResultItem {
  path: FilePath;
  content: string;
  matches: GitSearchMatch[];
}

export interface GitDiff {
  path: string;
  oldHash: string;
  newHash: string;
  status: StatusRow;
  text: string;
  fileContents?: [string, string];
}

export interface GitBlameLine {
  sha: string;
  author: string;
  email: string;
  lineNumber: number;
  text: string;
  title?: string;
  description?: string;
  date?: Date;
}

export interface GitAheadBehindStatus {
  ahead: string;
  behind: string;
}

export type GitCompareDirection = 'behind' | 'ahead';

export interface GitFileChange {
  path: FilePath;
  fileName: string;
  original?: FileContent;
  modified: FileContent;
  status: StatusRow;
}

export interface GitConflict {
  path: FilePath;
  content: string;
  base?: FileContent;
  yours?: FileContent;
  theirs?: FileContent;
  status: StatusRow;
}

export interface GitFSStatus {
  status:
    | 'init'
    | 'clone'
    | 'checkout'
    | 'fetch'
    | 'pull'
    | 'reset'
    | 'revert'
    | 'commit'
    | 'push'
    | 'read'
    | 'write'
    | 'delete'
    | 'checkout-conflict'
    | 'conflict'
    | 'merge'
    | 'stash';
  params?: unknown;
  error?: GitError;
}

export type GitBranchFrom = string | { use: GitSpecialBranch };
export type GitSpecialBranch = 'active' | 'default';

export type HeadStatus = 0 | 1 | 2;
export type WorkdirStatus = 0 | 1 | 2 | 3;
export type StageStatus = 0 | 1 | 2 | 3 | 4;
export type StatusRow = [HeadStatus, WorkdirStatus, StageStatus];

export interface GitRepoModelDefinition {
  fileName: string;
  property?: string;
}

export function isGitRepoModelDef(models: unknown): models is GitRepoModelDefinition {
  return !!(models as GitRepoModelDefinition)?.fileName;
}

/**
 * Converts a GitDiff to satisfy also GitFileChange interface.
 */
export function toFileChangeDiff(diff: GitDiff): GitDiff & GitFileChange {
  return {
    ...diff,
    fileName: diff.path.split('/').pop() || '',
    original: diff.fileContents![0],
    modified: diff.fileContents![1],
  };
}

interface IGitRepoStructureDefinition {
  /**
   * Specifies the model type.
   * Folder:
   * - "folderName" becomes the $name property.
   * - "folderName/metadata.json" will be spreaded into the returned object.
   *
   * File:
   * "fileName.json" becomes the $name property and the file will be
   * read and parsed and spreaded into the returned object.
   */
  modelType: 'folder' | 'file';
  /**
   * The model definitions, can define more.
   * "metadata" is implied as default
   */
  models?: {
    settings?: GitRepoModelDefinition;
    [propertyName: string]:
      | GitRepoModelDefinition
      | Record<
          string,
          {
            storeModelPropertyName: string;
          }
        >
      | undefined;
  };

  /**
   * The folder to search for children objects.
   * @default ".y42".
   */
  childFolder?: string;
}

interface GitRepoStructureChildTypeFileDefinition extends IGitRepoStructureDefinition {
  childType: 'file';
  childFolder: string;
}

interface GitRepoStructureChildTypeFolderDefinition extends IGitRepoStructureDefinition {
  childType?: 'folder';
}

export type GitRepoStructureDefinition =
  | GitRepoStructureChildTypeFileDefinition
  | GitRepoStructureChildTypeFolderDefinition;

export interface GitRepo {
  url: string;
  repoDir: string;
  spaceId: string;
  spaceName: string;

  userName: string;
  userEmail: string;
  authTokenFn: () => Promise<string>;

  /**
   * Branch to checkout at init.
   */
  branch?: string;
}

export const GIT_DEFAULT_BRANCH_NAME = 'main';
export const GIT_POSSIBLE_DEFAULT_BRANCH_NAMES = [GIT_DEFAULT_BRANCH_NAME, 'master'];
